import Loader from "reusable/mobx-helpers/Loader"

interface CacheConfig {
   cacheKey: string
}
export const cacheConfig: CacheConfig = {
   cacheKey: 'Generic-Cache-Key',
}

export function setCacheKey(cacheKey: string) {
   cacheConfig.cacheKey = cacheKey
}

export interface withCacheParams<T> {
   /**
    * promise or async function that will retrieve the external data silently (with no ui feedback)
    */
   getDataSilently: () => Promise<T>
   /**
    * promise or async function that will retrieve the external data silently (with ui feedback)
    */
   getDataWithLoading: () => Promise<T>
   /**
    * callback for doing stuff with the data (for example, setting state for rendering)
    */
   whatToDoWithData: (data: T) => Promise<any>
   /**
    * whether cache handler has to try to fetch updated data even if there is cache for that. Once fetched, will compare with the cache and, if different, will re-run whatToDoWithData()
    */
   silentlyRefreshData?: boolean
}

type CacheLocation = 'localStorage' | 'sessionStorage'

interface CacheHandlerProps<T> {
   key: string
   cacheLocation: CacheLocation
   transformationToCache?: (data: T) => any
   transformationFromCache?: (cacheData: any) => T
   useRawKey?: boolean
}

/**
 * returns a strongly-typed cache handler for complex objects
 * @param key unique key for this object cache
 * @param cacheLocation set cache location to local storage or session storage
 * @param transformationToCache callback to transform the data into simple json object that is cacheable
 * @param transformationFromCache callback to transform the json object from cache into the instatiated data
 */
export class CacheHandler<T> {
   static defaultCacheClearParams = {
      cacheLocation: 'localStorage',
   } as const satisfies CacheClearParams
   loader = new Loader()
   sp

   constructor(sp: CacheHandlerProps<T>) {
      this.sp = sp
   }

   get cacheKey() {
      if (this.sp.useRawKey) {
         return this.sp.key
      }
      return this.sp.key + cacheConfig.cacheKey
   }

   static isAuthMfaKey(key: string) {
      return key.startsWith('CognitoIdentityServiceProvider.') && [
         '.deviceKey',
         '.deviceGroupKey',
         '.randomPasswordKey',
      ].some((suffix) => key.endsWith(suffix))
   }

   static getAllKeys(cacheLocation: CacheLocation) {
      const storage: Storage = window[cacheLocation]
      const keys: string[] = []
      for (let i = 0; i < storage.length; i += 1) {
         const key = storage.key(i)
         if (key) {
            keys.push(key)
         }
      }

      return keys
   }

   /** Returns whether a storage key must survive clearAll. */
   static isPreservedKey(key: string) {
      const preservedKeyPrefixes = [
         'GapAppHideCompletedCheckListSteps',
         'GapAppDismissedAnnouncements',
         'meeting:ttv'
      ]

      return this.isAuthMfaKey(key) || preservedKeyPrefixes.some((prefix) => key.startsWith(prefix))
   }

   /** Clear all items in a given storage location; optionally preserving specific sets of items. */
   static clearAll(params?: Partial<CacheClearParams>) {
      const { cacheLocation } = { ...this.defaultCacheClearParams, ...(params || {}) }
      const storage: Storage = window[cacheLocation]

      this.getAllKeys(cacheLocation)
         .filter((key) => !this.isPreservedKey(key))
         .forEach((key) => storage.removeItem(key))
   }

   /**
    * retrieves external data with cache awareness
    * @param param0 options - see each option for documentation
    */
   async withCache(params: withCacheParams<T>): Promise<any> {
      await this.loader.executeLoading(async () => {
         const { getDataSilently, getDataWithLoading, whatToDoWithData, silentlyRefreshData = false } = params

         const cacheData = this.get()
         let cache = true
         if (typeof cacheData !== 'undefined') {
            await whatToDoWithData(cacheData)
         } else {
            cache = false
         }

         if (cache && !silentlyRefreshData) {
            return
         }

         let getData = getDataSilently
         if (!cache && getDataWithLoading) {
            getData = getDataWithLoading
         }

         let serverData: T | undefined

         try {
            serverData = await getData()
         } catch (error) {
            console.log(error)
            return
         }

         if (serverData !== undefined) {
            this.set(serverData)
            await whatToDoWithData(serverData)
         }
      })
   }

   /**
    * set object cache
    * @param data content to be cached
    */
   set = (data?: T) => {
      const previousContent =
         this.sp.cacheLocation === 'localStorage' ? localStorage[this.cacheKey] : sessionStorage[this.cacheKey]
      const newContent = this.stringifyToCache(data)

      if (previousContent === newContent) {
         return false
      }

      // Write to storage BEFORE dispatching the event so that any event
      // listener that reads from storage (e.g. useSyncExternalStore snapshot
      // functions) sees the new value, not the stale one.
      if (this.sp.cacheLocation === 'localStorage') {
         localStorage[this.cacheKey] = newContent
      } else {
         sessionStorage[this.cacheKey] = newContent
      }

      window.dispatchEvent(new CustomEvent(this.sp.key, {
         detail: JSON.stringify({
            previousContent: this.parseFromCache(previousContent),
            newContent: this.parseFromCache(newContent)
         })
      }))
      return true
   }

   /**
    * get object cache
    */
   get = () => {
      const data =
         this.sp.cacheLocation === 'localStorage' ? localStorage[this.cacheKey] : sessionStorage[this.cacheKey]
      if (data) return this.parseFromCache(data)
      else return undefined
   }

   /** Clears all items in this cache */
   clear = () => {
      this.set()
   }

   private stringifyToCache = (data?: T) => {
      if (!data && typeof data !== 'boolean') {
         return ''
      }
      if (this.sp.transformationToCache) {
         return JSON.stringify(this.sp.transformationToCache(data))
      } else {
         return JSON.stringify(data)
      }

   }

   private parseFromCache = (cacheData: any): T | undefined => {
      if (this.sp.transformationFromCache) {
         return this.sp.transformationFromCache(JSON.parse(cacheData))
      } else {
         return cacheData ? JSON.parse(cacheData) : undefined
      }
   }
}

interface CacheClearParams {
   cacheLocation: CacheLocation
}
